import cx from "classnames"
import { useState, useRef, useCallback, useLayoutEffect } from "react"
import { createPortal } from "react-dom"
import { Props } from "./DropdownMenu.type"
import "./DropdownMenu.css"
import { useOutside } from "src/shared/hooks/handle-click-outside"

// ancho declarado en DropdownMenu.css, usado como estimado antes de medir el menú
const MENU_WIDTH = 233
const VIEWPORT_MARGIN = 8

function DropdownMenu(props: Props): JSX.Element {
    const { className = "", containerClassName = "", children, items = [], config = { top: 0, left: 0 } } = props
    const [visible, setVisible] = useState<boolean>(false)
    const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 })

    const dropRef = useRef<HTMLDivElement>(null)
    const elementRef = useRef<HTMLDivElement>(null)

    useOutside(dropRef, () => setVisible(false))

    // el menú se renderiza en un portal con position fixed, así que se posiciona con
    // coordenadas de viewport y no se desfasa cuando el contenedor tiene scroll
    const updatePosition = useCallback(() => {
        const trigger = elementRef.current
        if (!trigger) return

        const rect = trigger.getBoundingClientRect()
        const menuWidth = dropRef.current?.offsetWidth || MENU_WIDTH
        const menuHeight = dropRef.current?.offsetHeight || 0

        let top = rect.bottom + config.top
        // si no cabe abajo, se abre hacia arriba del botón
        if (menuHeight && top + menuHeight > window.innerHeight - VIEWPORT_MARGIN) {
            top = Math.max(VIEWPORT_MARGIN, rect.top - menuHeight - config.top)
        }

        const left = config.left > 0 ? rect.left - config.left : rect.right - menuWidth

        setPosition({
            top,
            left: Math.min(Math.max(VIEWPORT_MARGIN, left), window.innerWidth - menuWidth - VIEWPORT_MARGIN),
        })
    }, [config.top, config.left])

    useLayoutEffect(() => {
        if (!visible) return

        updatePosition()

        // true en el listener para escuchar el scroll de cualquier contenedor, no solo el de la página
        window.addEventListener("scroll", updatePosition, true)
        window.addEventListener("resize", updatePosition)
        return () => {
            window.removeEventListener("scroll", updatePosition, true)
            window.removeEventListener("resize", updatePosition)
        }
    }, [visible, updatePosition])

    return (
        <>
            <div className={containerClassName} ref={elementRef} onClick={() => setVisible(true)}>
                {children}
            </div>
            {visible &&
                createPortal(
                    <div
                        ref={dropRef}
                        className={cx("dropdown-menu", "dropdown-menu__animation", className)}
                        style={{ top: position.top, left: position.left }}
                    >
                        {items?.map(({ label = "", onClick, disabled, testId = "" }, index) => (
                            <div
                                className="dropdown-menu__item"
                                key={index}
                                onClick={() => {
                                    if (!disabled) {
                                        setVisible(false)
                                        onClick()
                                    }
                                }}
                                style={{ opacity: disabled ? 0.5 : 1 }}
                                data-testid={testId}
                            >
                                {label}
                            </div>
                        ))}
                    </div>,
                    document.body
                )}
        </>
    )
}

export default DropdownMenu
